import React from 'react';
import { Badge, Button, Divider, InputNumber, Table, Typography } from 'antd';
import { Price } from '../../../components/Display';
import { UploadFile } from '@partsbadger/library';
import { PartDimensions, PartPreview } from '../../../components/Quotes/index';
import { DynamicItemsProps, QuoteLineItemProps } from '../../../types/types';
import { ColumnProps } from 'antd/es/table';

let timeout: ReturnType<typeof setTimeout>;

interface Props {
    dynamic_items: DynamicItemsProps[];
    editable: boolean;
    handleChange?: (data: QuoteLineItemProps) => void;
    handleDelete?: (data: QuoteLineItemProps, dynamic: boolean) => void;
    setLineItems?: any;
    setDynamicItems?: any;
    record?: DynamicItemsProps;
    handleMorePrices?: (item_id: number, variation_id: number, part_name: string) => void;
}

export const LineItems = React.forwardRef((props: Props, ref) => {
    const { dynamic_items, editable = false, handleChange, handleDelete, handleMorePrices, setDynamicItems } = props;
    const onChange = (record: any) => {
        if (timeout) {
            clearTimeout(timeout);
        }

        timeout = setTimeout(() => handleChange && handleChange(record), 400);
    };

    const setFiles = (data: {
        files2d?: number;
        files3d?: number;
        record: { item: QuoteLineItemProps; variation: any };
    }) => {
        const lineItems = dynamic_items.map((current_item: any) => {
            if (data.record?.item?.id && data.record?.item?.id === current_item.item?.id) {
                if (data.files2d) {
                    data.record.item.production_2d_file = data.files2d;
                }

                if (data.files3d) {
                    data.record.item.production_3d_file = data.files3d;
                }

                return { item: data.record.item, variation: data.record.variation };
            }
            return current_item;
        });

        setDynamicItems(lineItems);
    };

    const columns: ColumnProps<DynamicItemsProps>[] = [
        {
            title: 'Part Information',
            dataIndex: 'name',
            render: (text: string, record: DynamicItemsProps) => (
                <React.Fragment>
                    <div className="flex justify-center">
                        <div className="w-1/4">
                            <PartPreview
                                thumbnail={record.item.product?.attachments?.file3d?.image}
                                has_file_3d={record.item?.product?.has_file_3d}
                            />
                        </div>
                        <div className="w-3/4">
                            <div>{record.item.name}</div>
                            <div>
                                <PartDimensions {...record?.item.product?.dimensions_dict} />
                            </div>
                            <div>
                                <div>
                                    <span>
                                        <span className={'font-bold'}>Material: </span>
                                        {record.item.product.material}
                                    </span>
                                    <Divider type={'vertical'} />
                                    <span>
                                        <span className={'font-bold'}>Surface Finish: </span>
                                        {record.item.product.finishes.map((value: any) => value)}
                                    </span>
                                    <Divider type={'vertical'} />
                                    <span>
                                        <span className={'font-bold'}>Coating: </span>
                                        {record.item.product.coatings.map((value: any) => value)}
                                    </span>
                                    <Divider type={'vertical'} /> <br />
                                    <span>
                                        <span className={'font-bold'}>Process: </span>
                                        {record.item.manufacturing_type}
                                    </span>
                                    <Divider type={'vertical'} />
                                </div>

                                <div className={'flex flex-col'}>
                                    <div className={'flex flex lg:flex-row md:flex-col sm:flex-col'}>
                                        {record.item.product.has_file_3d && (
                                            <div>
                                                <Typography.Text strong>3D File: </Typography.Text>
                                                <span>{record.item?.product?.attachments?.file3d?.filename}</span>
                                            </div>
                                        )}

                                        {editable && (
                                            <>
                                                {!record.item.product.has_file_3d && (
                                                    <div className="px-2">
                                                        <Badge count="3D Required" />
                                                        &nbsp;
                                                        <UploadFile
                                                            fileType={'3D'}
                                                            renderAs={'button'}
                                                            onUpload={file => {
                                                                setFiles({
                                                                    files3d: file.response.id,
                                                                    record: {
                                                                        item: record.item,
                                                                        variation: record.variation,
                                                                    },
                                                                });
                                                            }}
                                                        />
                                                    </div>
                                                )}
                                            </>
                                        )}

                                        <Divider type="vertical" />

                                        {record.item.product.has_file_2d && (
                                            <div>
                                                <Typography.Text strong>2D File: </Typography.Text>
                                                <span>{record.item.product.attachments.files_2d.filename}</span>
                                            </div>
                                        )}

                                        {editable && (
                                            <>
                                                {!record.item.product.has_file_2d && (
                                                    <div className="px-2">
                                                        <Badge count="2D Required" />
                                                        &nbsp;
                                                        <UploadFile
                                                            fileType={'2D'}
                                                            renderAs={'button'}
                                                            onUpload={(file: any) => {
                                                                setFiles({
                                                                    files2d: file.response.id,
                                                                    record: {
                                                                        item: record.item,
                                                                        variation: record.variation,
                                                                    },
                                                                });
                                                            }}
                                                        />
                                                    </div>
                                                )}
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </React.Fragment>
            ),
        },
        {
            title: 'Qty',
            dataIndex: 'quantity',
            align: 'center',
            render: (text: string, record: any) => {
                return editable &&
                    record.item.is_dynamic &&
                    record.item.quantity_range &&
                    record.item.has_dynamic_quantities === false ? (
                    <InputNumber
                        title={`Insert value between ${record.item.quantity_range.lower} - ${record.item.quantity_range.upper}`}
                        min={record.item.quantity_range.lower}
                        max={record.item.quantity_range.upper}
                        defaultValue={record.variation.quantity}
                        onChange={value => {
                            if (
                                value &&
                                record.item.quantity_range.lower <= value &&
                                value <= record.item.quantity_range.upper
                            ) {
                                onChange({
                                    ...record.item,
                                    quantity: value,
                                });
                            }
                        }}
                    />
                ) : (
                    <div>
                        {record.variation.quantity}
                        <br />
                        {editable && record.item.has_dynamic_quantities && (
                            <>
                                <Button
                                    type="link"
                                    onClick={() => {
                                        handleMorePrices &&
                                            handleMorePrices(record.item.id, record.variation.id, record.item.name);
                                    }}
                                >
                                    Change
                                </Button>
                            </>
                        )}
                    </div>
                );
            },
        },
        {
            title: 'Estimated Lead Time',
            dataIndex: 'lead_time',
            render: (text: string, record: DynamicItemsProps) => {
                return `${record.variation.lead_time} days`;
            },
            align: 'center',
        },
        {
            title: 'Unit Price',
            dataIndex: 'unit_price',
            render: (text: string, record: DynamicItemsProps) => {
                return <Price value={record.variation.unit_price} />;
            },
            align: 'right',
        },
        {
            title: 'Total Price',
            dataIndex: 'total_price',
            render: (text: string, record: DynamicItemsProps) => {
                if (record.item.has_dynamic_quantities) {
                    return <Price value={record.variation.total_price} />;
                } else {
                    return <Price value={record.variation.total_after_discount} />;
                }
            },
            align: 'right',
        },
    ];

    if (editable) {
        columns.push({
            align: 'right',
            dataIndex: 'actions',
            title: 'Actions',
            render: (text: any, record: any) => {
                return (
                    <React.Fragment>
                        <div>
                            <span
                                className={'link'}
                                onClick={() => {
                                    if (handleDelete && record.item.has_dynamic_quantities) {
                                        handleDelete(record, true);
                                    } else if (handleDelete) {
                                        handleDelete(record, false);
                                    }
                                }}
                            >
                                Remove
                            </span>
                        </div>
                    </React.Fragment>
                );
            },
        });
    }

    return (
        <>
            <Table
                columns={columns}
                dataSource={dynamic_items}
                pagination={false}
                scroll={window.innerWidth <= 600 ? { x: 600 } : undefined}
            />
        </>
    );
});
